import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, Select, Empty, Typography, Tag, Spin } from 'antd';
import Chart from 'react-apexcharts';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRegimeOverlay, selectors } from 'state';
import type { RegimeOverlayAssetType } from 'state';

/* Regime slug → label + color (matches the spec / studio tag colors). */
const REGIME_META: Record<string, { label: string; color: string }> = {
  uptrend: { label: 'Uptrend', color: '#0e9f6e' },
  downtrend: { label: 'Downtrend', color: '#d14343' },
  range_bound: { label: 'Range-bound', color: '#868e96' },
  high_volatility: { label: 'High Volatility', color: '#d48806' },
  breakout: { label: 'Breakout', color: '#0f5fd6' },
  reversal: { label: 'Reversal', color: '#7048e8' },
};

/* Build the price line + regime overlay (spans as shaded bands, single-period
 * reversal/breakout markers as vertical lines) for one asset. */
const buildChart = (asset: RegimeOverlayAssetType, dates: string[]) => {
  const data = dates.map(d => {
    const v = asset.prices[d];
    return v == null ? null : v;
  });
  const annotations = asset.segments.map(s => {
    const meta = REGIME_META[s.regime] ?? { label: s.regime, color: '#888' };
    if (s.start_date === s.end_date) {
      return {
        x: s.start_date,
        borderColor: meta.color,
        label: {
          text: meta.label,
          orientation: 'horizontal',
          style: { background: meta.color, color: '#fff', fontSize: '10px' },
        },
      };
    }
    return {
      x: s.start_date,
      x2: s.end_date,
      fillColor: meta.color,
      opacity: 0.12,
      label: {
        text: meta.label,
        style: {
          color: '#5c6b7f',
          fontSize: '10px',
          background: 'transparent',
        },
      },
    };
  });
  return {
    series: [{ name: asset.ticker || asset.name, data }],
    options: {
      chart: {
        type: 'line',
        animations: { enabled: false },
        toolbar: { show: false },
        // Don't hijack vertical scroll: no drag-zoom, no mouse-wheel zoom.
        zoom: { enabled: false, allowMouseWheelZoom: false },
      },
      stroke: { width: 2, curve: 'straight' },
      colors: ['#2b3648'],
      xaxis: {
        categories: dates,
        tickAmount: Math.min(12, dates.length),
        labels: { rotate: -45, hideOverlappingLabels: true },
      },
      yaxis: {
        labels: {
          formatter: (v: number) => (v == null ? '' : Number(v).toFixed(2)),
        },
      },
      annotations: { xaxis: annotations },
      markers: { size: 0 },
      dataLabels: { enabled: false },
      grid: { borderColor: '#eef1f4' },
    } as object,
  };
};

/* One asset card (memoised so filtering doesn't rebuild every chart). */
const RegimeAssetChart: React.FC<{
  asset: RegimeOverlayAssetType;
  dates: string[];
}> = React.memo(({ asset, dates }) => {
  const { series, options } = useMemo(
    () => buildChart(asset, dates),
    [asset, dates],
  );
  return (
    <div
      style={{
        border: '1px solid #eef1f4',
        borderRadius: 10,
        padding: '8px 10px',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: '#2b3648' }}>
        {asset.ticker} · {asset.name}{' '}
        <span style={{ fontWeight: 400, color: '#8c98a8' }}>
          ({asset.asset_class}) · {asset.segments.length} seg
        </span>
      </div>
      <Chart series={series} options={options} type="line" height={240} />
    </div>
  );
});
RegimeAssetChart.displayName = 'RegimeAssetChart';

/**
 * Admin helper: fetches the session's per-asset price curves (whole session) +
 * the latest segmentation and stacks one overlay chart per asset. Filter by
 * asset class (chips) and/or search-select specific assets.
 */
const RegimeVisualizerDrawer: React.FC<{
  sessionId: number;
  open: boolean;
  onClose: () => void;
}> = ({ sessionId, open, onClose }) => {
  const dispatch = useDispatch();
  const overlay = useSelector(selectors.regimeOverlay);
  const fetching = useSelector(selectors.regimeOverlayFetching);
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState<number[]>([]);

  useEffect(() => {
    if (open) {
      dispatch(fetchRegimeOverlay({ session_id: sessionId }));
    }
  }, [open, sessionId, dispatch]);

  // Reset filters when the session changes (kept across open/close otherwise).
  useEffect(() => {
    setSelectedClasses([]);
    setSelectedAssetIds([]);
  }, [sessionId]);

  const classes = useMemo(() => {
    const set = new Set((overlay?.assets ?? []).map(a => a.asset_class));
    return [...set].sort();
  }, [overlay]);

  const visibleAssets = useMemo(() => {
    const assets = overlay?.assets ?? [];
    const classSet = new Set(selectedClasses);
    const idSet = new Set(selectedAssetIds);
    return assets.filter(
      a =>
        (classSet.size === 0 || classSet.has(a.asset_class)) &&
        (idSet.size === 0 || idSet.has(a.underlying_asset_id)),
    );
  }, [overlay, selectedClasses, selectedAssetIds]);

  const toggleClass = (c: string, checked: boolean) =>
    setSelectedClasses(prev =>
      checked ? [...prev, c] : prev.filter(x => x !== c),
    );

  const dates = overlay?.dates ?? [];

  return (
    <Drawer
      title="Market regimes — price overlay"
      placement="right"
      width="80%"
      open={open}
      onClose={onClose}
      styles={{ body: { height: '100%' } }}
    >
      {fetching && !overlay ? (
        <Spin />
      ) : !overlay || !overlay.assets.length ? (
        <Empty description="No price data for this session." />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            gap: 12,
          }}
        >
          {/* Filters + meta (fixed) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              {overlay.report ? (
                <Typography.Text type="secondary">
                  Report #{overlay.report.id} ·{' '}
                  {overlay.report.model_name || overlay.report.provider}
                  {overlay.report.generated_at
                    ? ` · ${overlay.report.generated_at.replace('T', ' ').slice(0, 16)}`
                    : ''}
                </Typography.Text>
              ) : (
                <Typography.Text type="warning">
                  No regime report yet — click “Compute market regimes”.
                </Typography.Text>
              )}
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                {Object.values(REGIME_META).map(m => (
                  <span
                    key={m.label}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 3,
                        background: m.color,
                        flex: '0 0 auto',
                      }}
                    />
                    <span style={{ fontSize: 12, color: '#2b3648' }}>
                      {m.label}
                    </span>
                  </span>
                ))}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ color: '#8c98a8', fontSize: 12 }}>Classes:</span>
              {classes.map(c => (
                <Tag.CheckableTag
                  key={c}
                  checked={selectedClasses.includes(c)}
                  onChange={checked => toggleClass(c, checked)}
                >
                  {c}
                </Tag.CheckableTag>
              ))}
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Search & select assets…"
                style={{ minWidth: 320, flex: 1 }}
                value={selectedAssetIds}
                onChange={v => setSelectedAssetIds(v as number[])}
                maxTagCount="responsive"
                options={overlay.assets.map(a => ({
                  value: a.underlying_asset_id,
                  label: `${a.ticker} · ${a.name} (${a.asset_class})`,
                }))}
              />
            </div>

            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Showing {visibleAssets.length} of {overlay.assets.length} assets
            </Typography.Text>
          </div>

          {/* Stacked, scrollable charts */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              paddingRight: 4,
            }}
          >
            {visibleAssets.length ? (
              visibleAssets.map(a => (
                <RegimeAssetChart
                  key={a.underlying_asset_id}
                  asset={a}
                  dates={dates}
                />
              ))
            ) : (
              <Empty description="No assets match the current filters." />
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default RegimeVisualizerDrawer;
